/**
 * Profile / role selection helpers.
 *
 * A "role" is the user-facing choice (Manager, OMR, Combined). Each role
 * maps to a backend profile use_case_type. When a paused session is resumed
 * under a different role than it was started with, the effective profile is
 * upgraded to the combined profile — a superset of both — so previously
 * answered fields are reused and questions from both roles are asked/shown.
 * This mirrors the backend's _resolve_resume_use_case_type logic.
 */

import {
  USE_CASE_INITIAL_INFORMATION,
  USE_CASE_INITIAL_ENCOUNTER,
  USE_CASE_INITIAL_INFORMATION_AND_ENCOUNTER,
} from '../constants';

export type Role = 'whs_manager' | 'omr' | 'combined';

/** Map a user-facing role to its backend profile use_case_type. */
export const roleToUseCase = (role: Role): string => {
  switch (role) {
    case 'omr':
      return USE_CASE_INITIAL_ENCOUNTER;
    case 'combined':
      return USE_CASE_INITIAL_INFORMATION_AND_ENCOUNTER;
    case 'whs_manager':
    default:
      return USE_CASE_INITIAL_INFORMATION;
  }
};

/** Best-effort reverse map from a use_case_type back to a role (for pre-selecting). */
export const useCaseToRole = (useCase: string | undefined | null): Role => {
  switch (useCase) {
    case USE_CASE_INITIAL_ENCOUNTER:
      return 'omr';
    case USE_CASE_INITIAL_INFORMATION_AND_ENCOUNTER:
      return 'combined';
    case USE_CASE_INITIAL_INFORMATION:
    default:
      return 'whs_manager';
  }
};

// Profiles that combine into the combined (Initial Information + Encounter) profile.
const COMBINABLE_USE_CASES = new Set<string>([
  USE_CASE_INITIAL_INFORMATION,
  USE_CASE_INITIAL_ENCOUNTER,
  USE_CASE_INITIAL_INFORMATION_AND_ENCOUNTER,
]);

/**
 * Decide which profile to record under when resuming a session.
 *
 * If the newly selected profile differs from the one the session was started
 * with and both are combinable Initial Information/Encounter profiles, upgrade
 * to the combined profile. Otherwise keep the selection as-is.
 */
export const resolveResumeUseCase = (storedUseCase: string, selectedUseCase: string): string => {
  if (storedUseCase === selectedUseCase) {
    return selectedUseCase;
  }
  if (COMBINABLE_USE_CASES.has(storedUseCase) && COMBINABLE_USE_CASES.has(selectedUseCase)) {
    return USE_CASE_INITIAL_INFORMATION_AND_ENCOUNTER;
  }
  return selectedUseCase;
};
